import { useEffect, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { usePassIds } from "../hooks/useBarrels";

interface AddLayoutModalProps {
	open: boolean;
	standId: number | null;
	onClose: () => void;
	onSave: (passId: number) => void;
	isSaving?: boolean;
}

export const AddLayoutModal = ({
	open,
	standId,
	onClose,
	onSave,
	isSaving = false,
}: AddLayoutModalProps) => {
	const [selectedPassId, setSelectedPassId] = useState<number | null>(null);

	const { data: passIds = [], isLoading } = usePassIds({
		standId,
		enabled: open && standId !== null,
	});

	const selectedPass = passIds.find((p) => p.Id === selectedPassId) ?? null;

	useEffect(() => {
		if (open) {
			setSelectedPassId(null);
		}
	}, [open]);

	const handleSave = () => {
		if (selectedPassId) {
			onSave(selectedPassId);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>ADD PASS</DialogTitle>
			<DialogContent dividers>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
					<Autocomplete
						options={passIds}
						value={selectedPass}
						size="small"
						fullWidth
						autoHighlight
						loading={isLoading}
						disabled={isSaving}
						getOptionLabel={(pass) => pass.Name}
						isOptionEqualToValue={(option, current) => option.Id === current.Id}
						onChange={(_event, pass) => setSelectedPassId(pass?.Id ?? null)}
						renderOption={(props, pass) => {
							const { key, ...rest } = props as typeof props & { key: string };
							return (
								<li key={pass.Id} {...rest}>
									{pass.Name}
									{pass.PassTypeName && ` - ${pass.PassTypeName}`}
									{pass.PassShapeName && ` (${pass.PassShapeName})`}
								</li>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Pass ID"
								required
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						)}
					/>

					<TextField
						label="Scheduled Tonnage"
						value={
							selectedPass?.ScheduledTonnage != null
								? selectedPass.ScheduledTonnage.toLocaleString("en-US", {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})
								: ""
						}
						fullWidth
						size="small"
						disabled
						slotProps={{
							input: { readOnly: true },
							inputLabel: { shrink: true },
						}}
					/>

					{selectedPass &&
						(selectedPass.PassTypeName || selectedPass.PassShapeName) && (
							<Box sx={{ display: "flex", gap: 2 }}>
								{selectedPass.PassTypeName && (
									<Typography variant="body2">
										<strong>Type:</strong> {selectedPass.PassTypeName}
									</Typography>
								)}
								{selectedPass.PassShapeName && (
									<Typography variant="body2">
										<strong>Shape:</strong> {selectedPass.PassShapeName}
									</Typography>
								)}
							</Box>
						)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onClose}
					disabled={isSaving}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					disabled={!selectedPassId || isSaving}
				>
					{isSaving ? "SAVING..." : "SAVE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
